import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Loader2,
  PackageCheck,
  ShieldCheck,
} from 'lucide-react';
import { dispatchService } from '@/services/dispatch';
import { securitySealsService } from '@/services/securitySeals';
import type { DispatchOrder, DispatchOrderItem } from '@/types/dispatch';
import { ApiError } from '@/lib/errors';
import { BarcodeScanner } from './BarcodeScanner';
import { haptics } from './haptics';

/**
 * Pack flow — native layout.
 *
 *   Header (large title with dispatch number)
 *   Scrolling line list
 *   Sticky bottom drawer with the two scanners + progress for the
 *     currently-selected line
 *
 * Operator pattern:
 *   1. Tap a line in the list (active = primary saffron tint)
 *   2. Bottom drawer reveals the SKU scanner
 *   3. Scan SKU → BE confirms it matches; line's "SKU x/y" advances
 *   4. Scanner auto-shifts focus to seal field
 *   5. Scan seal → "Seal x/y" advances
 *   6. When SKU + Seal both hit y/y the line shows a checkmark and
 *      operator picks the next line.
 */
export function PwaPackScanPage() {
  const { dispatchId } = useParams<{ dispatchId: string }>();
  const navigate = useNavigate();
  const [dispatch, setDispatch] = useState<DispatchOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeLineId, setActiveLineId] = useState<string | null>(null);
  const [sealCounts, setSealCounts] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!dispatchId) return;
    setError(null);
    try {
      const d = await dispatchService.get(dispatchId);
      setDispatch(d);
      setActiveLineId((prev) =>
        prev && d.items.some((i) => i.id === prev) ? prev : (d.items[0]?.id ?? null),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dispatch');
    }
  }, [dispatchId]);

  useEffect(() => {
    void load();
  }, [load]);

  const activeLine = useMemo(
    () => dispatch?.items.find((i) => i.id === activeLineId) ?? null,
    [dispatch, activeLineId],
  );

  const onSkuScan = useCallback(
    async (raw: string) => {
      if (!dispatch || busy) return;
      const code = raw.trim();
      setBusy(true);
      try {
        const updated = await dispatchService.scan(dispatch.id, { sku: code });
        setDispatch(updated);
        haptics.tap();
        toast.success(`SKU verified: ${code}`);
      } catch (err) {
        haptics.warn();
        toast.error(err instanceof ApiError ? err.message : 'Scan failed');
      } finally {
        setBusy(false);
      }
    },
    [dispatch, busy],
  );

  const onSealScan = useCallback(
    async (raw: string) => {
      if (!activeLine || busy) return;
      const code = raw.trim().toUpperCase();
      setBusy(true);
      try {
        const seals = await securitySealsService.applyToLine(activeLine.id, {
          codes: [code],
        });
        setSealCounts((prev) => ({
          ...prev,
          [activeLine.id]: (prev[activeLine.id] ?? 0) + seals.length,
        }));
        haptics.success();
        toast.success(`Seal ${code} applied`);
      } catch (err) {
        haptics.warn();
        toast.error(err instanceof ApiError ? err.message : 'Could not apply seal');
      } finally {
        setBusy(false);
      }
    },
    [activeLine, busy],
  );

  // Pack → ready, on the handheld. The desktop "Pack & verify" panel
  // is the only other place a dispatch goes PENDING → READY_FOR_PICKUP;
  // without this the bench operator has to walk back to a desktop just
  // to release a fully-scanned dispatch for pickup. `forcePartial` lets
  // them release a split pickup (some lines short) when they choose to.
  const markReady = useCallback(
    async (forcePartial: boolean) => {
      if (!dispatch || busy) return;
      setBusy(true);
      try {
        await dispatchService.updateStatus(dispatch.id, {
          status: 'READY_FOR_PICKUP',
          forcePartial,
        });
        haptics.success();
        toast.success(
          forcePartial
            ? `${dispatch.dispatchNumber} marked partial — ready for pickup`
            : `${dispatch.dispatchNumber} ready for pickup`,
        );
        navigate('/pwa/pack');
      } catch (err) {
        haptics.warn();
        toast.error(
          err instanceof ApiError ? err.message : 'Could not mark ready for pickup',
        );
      } finally {
        setBusy(false);
      }
    },
    [dispatch, busy, navigate],
  );

  if (!dispatchId) {
    return (
      <div className="px-4 py-8 text-center text-[13px] text-muted-foreground">
        No dispatch id in the URL.
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-4 pt-4">
        <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-[13px] text-destructive">
          {error}
        </div>
      </div>
    );
  }
  if (!dispatch) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalUnits = dispatch.items.reduce((sum, i) => sum + Number(i.quantity), 0);
  const totalSeals = Object.values(sealCounts).reduce((s, n) => s + n, 0);
  // SKU completeness is the actual BE gate for READY_FOR_PICKUP; seals
  // are advisory (their enforcement lives on the returns side), so we
  // surface seal progress but never hard-block the release on it.
  const allScanned = dispatch.items.every(
    (i) => Number(i.scannedQuantity) >= Number(i.quantity),
  );
  const allSealed = totalSeals >= totalUnits;

  return (
    <div className="pwa-anim-in flex flex-col gap-4 pb-[360px] pt-3">
      <div className="px-4">
        <p className="text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
          Packing
        </p>
        <h1 className="font-mono text-[24px] font-bold leading-tight tracking-tight text-foreground">
          {dispatch.dispatchNumber}
        </h1>
        <p className="mt-1 text-[12.5px] text-muted-foreground">
          {dispatch.salesOrder?.customer?.name} · Order{' '}
          {dispatch.salesOrder?.orderNumber}
        </p>
        <div className="mt-3 flex items-center gap-2 text-[11.5px]">
          <span className="rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
            <ShieldCheck className="mr-1 inline h-3 w-3" />
            {totalSeals} / {totalUnits} sealed
          </span>
        </div>
      </div>

      <ul className="space-y-1.5 px-4">
        {dispatch.items.map((line) => (
          <LineRow
            key={line.id}
            line={line}
            active={line.id === activeLineId}
            sealCount={sealCounts[line.id] ?? 0}
            onPick={() => setActiveLineId(line.id)}
          />
        ))}
      </ul>

      {/* ─── Sticky bottom (sits ABOVE the tab bar) ──────────────────
          Once every line's SKU count is satisfied there's nothing left
          to scan, so the scanner drawer gives way to the completion
          footer that releases the dispatch for pickup. */}
      {allScanned ? (
        <div
          className="pwa-pl-safe pwa-pr-safe pwa-anim-in fixed inset-x-0 z-20 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85"
          style={{
            bottom:
              'calc(var(--pwa-tab-bar-h, 64px) + env(safe-area-inset-bottom))',
          }}
        >
          <div className="space-y-2.5 px-4 pb-3 pt-3">
            <div className="flex items-center gap-2 text-[12.5px] font-medium text-success">
              <CheckCircle2 className="h-4 w-4" />
              All {totalUnits} units scanned
            </div>
            {!allSealed && (
              <p className="text-[11.5px] text-muted-foreground">
                {totalSeals} / {totalUnits} sealed — you can still release for
                pickup, seals are tracked separately.
              </p>
            )}
            <button
              type="button"
              onClick={() => void markReady(false)}
              disabled={busy}
              className="pwa-tap flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-3.5 text-[15px] font-semibold text-primary-foreground shadow-sm active:bg-primary/90 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <PackageCheck className="h-5 w-5" />
              )}
              Mark ready for pickup
            </button>
          </div>
        </div>
      ) : (
        activeLine && (
          <div
            className="pwa-pl-safe pwa-pr-safe pwa-anim-in fixed inset-x-0 z-20 border-t border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/85"
            style={{
              bottom:
                'calc(var(--pwa-tab-bar-h, 64px) + env(safe-area-inset-bottom))',
            }}
          >
            <div className="space-y-3 px-4 pb-3 pt-3">
              <div className="flex items-center justify-between gap-2 text-[11.5px] text-muted-foreground">
                <span>
                  Active:{' '}
                  <span className="font-mono text-foreground">
                    {activeLine.sku}
                  </span>{' '}
                  · {activeLine.name}
                </span>
                <button
                  type="button"
                  onClick={() => void markReady(true)}
                  disabled={busy}
                  className="pwa-tap shrink-0 rounded-full border border-border px-2.5 py-1 text-[11px] font-medium text-muted-foreground active:bg-muted/50 disabled:opacity-50"
                  title="Release for pickup even though some lines aren't fully scanned (split pickup)."
                >
                  Mark partial
                </button>
              </div>
              <BarcodeScanner
                label="1. Scan SKU"
                placeholder="Scan item barcode"
                onSubmit={onSkuScan}
                disabled={busy}
              />
              <BarcodeScanner
                label="2. Scan seal"
                placeholder="Scan plastic tag code"
                onSubmit={onSealScan}
                disabled={busy}
              />
            </div>
          </div>
        )
      )}
    </div>
  );
}

function LineRow({
  line,
  active,
  sealCount,
  onPick,
}: {
  line: DispatchOrderItem;
  active: boolean;
  sealCount: number;
  onPick: () => void;
}) {
  const lineQty = Number(line.quantity);
  const skuQty = Number(line.scannedQuantity);
  const skuDone = skuQty >= lineQty;
  const sealsDone = sealCount >= lineQty;
  const allDone = skuDone && sealsDone;
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        className={`pwa-tap flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left ${
          active
            ? 'border-primary bg-primary/10 shadow-sm'
            : 'border-border/70 bg-card active:bg-muted/40'
        }`}
      >
        <div
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
            allDone ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'
          }`}
        >
          {allDone ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <span className="text-[14px] font-semibold">{lineQty}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono text-[13px] font-semibold text-foreground">
            {line.sku}
          </div>
          <div className="truncate text-[11.5px] text-muted-foreground">
            {line.name}
          </div>
          <div className="mt-1 flex gap-1.5 text-[10.5px]">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                skuDone
                  ? 'bg-success/15 text-success'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              SKU {skuQty}/{lineQty}
            </span>
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                sealsDone
                  ? 'bg-success/15 text-success'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              Seal {sealCount}/{lineQty}
            </span>
          </div>
        </div>
      </button>
    </li>
  );
}
